<template>
	<p>{{ text.join('') }}</p>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const { language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
}>();

const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Tällä hetkellä voit katsoa elokuvan',
					'suoratoistona palvelussa'
				),
				getOfferTextBlock(MonetizationType.Ads, ' tai ilmaiseksi mainosten kanssa palvelussa'),
				'. ',
			];
		case 'ru':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В настоящее время Вы можете посмотреть', 'на'),
				getOfferTextBlock(MonetizationType.Ads, ' или бесплатно с рекламой на'),
				'. ',
			];
		case 'it':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Adesso puoi guardare', 'in streaming su'),
				getOfferTextBlock(MonetizationType.Ads, ' o gratuitamente con avvisi pubblicitari su'),
				'. ',
			];
		case 'de':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Du kannst', 'im Abo bei'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' oder bei',
					'kostenloas mit Werbeunterbrechungen im Stream anschauen'
				),
				'. ',
			];
		case 'fr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'En ce moment, vous pouvez regarder', 'en streaming sur'),
				getOfferTextBlock(MonetizationType.Ads, ' ou gratuit avec publicités sur'),
				'. ',
			];
		case 'es':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Actualmente, usted es capaz de ver', 'streaming en'),
				getOfferTextBlock(MonetizationType.Ads, ' o forma gratuita con anuncios en'),
				'. ',
			];
		case 'ja':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'は', 'で配信中です。'),
				getOfferTextBlock(MonetizationType.Ads, ' 広告付きの動画は', 'で無料配信中です'),
				'。',
			];
		case 'ko':
			return [
				getOfferTextBlock(MonetizationType.Flatrate, '현재 ', `에서 "${props.title}" 스트리밍 서비스 중이며 `),
				getOfferTextBlock(MonetizationType.Ads, '광고가 포함된 영상은 ', '에서 무료 배포 중입니다.'),
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'În prezent, puteți viziona', 'streaming pe'),
				getOfferTextBlock(MonetizationType.Ads, ' sau gratuit cu afișare de anunțuri pe'),
				'. ',
			];
		case 'hu':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Jelenleg a(z)', 'adatfolyamként elérhető a(z)'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' szolgáltatónál, vagy megtekinthető ingyenesen reklámokkal itt:'
				),
				'. ',
			];
		case 'cs':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktuálně máte možnost sledovat', 'streaming u'),
				getOfferTextBlock(MonetizationType.Ads, 'nebo zdarma s reklamami u'),
				'. ',
			];
		case 'bg':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В момента можете да гледате стрийминг на', 'в', 'онлайн'),
				getOfferTextBlock(MonetizationType.Ads, ' или безплатно с реклами в'),
				'. ',
			];
		case 'ar':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'يمكنكم حالياً مشاهدة', 'على'),
				getOfferTextBlock(MonetizationType.Ads, ' أو مجاناً مع فواصل إعلانية على'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Atualmente você pode assistir', 'em streaming via'),
				getOfferTextBlock(MonetizationType.Ads, ' gratuitamente com anúncios'),
				'. ',
			];
		case 'tr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Şu an', 'adlı yapımı'),
				getOfferTextBlock(MonetizationType.Ads, ' platformunda ücretsiz ama reklamlı olarak izleyebilirsiniz'),
				'. ',
			];
		case 'sq':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktualisht, ju mund të shikoni', 'në'),
				getOfferTextBlock(MonetizationType.Ads, ' apo falas me reklama në'),
				'. ',
			];
		case 'pl':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Obecnie możesz oglądać', 'w streamingu w serwisie'),
				getOfferTextBlock(MonetizationType.Ads, ' lub za darmo z reklamami w serwisie'),
				'. ',
			];
		case 'az':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Hazırda siz', 'üzərindən'),
				getOfferTextBlock(
					MonetizationType.Ads,
					'yayımını',
					'üzərindən reklamlarla ödənişsiz izləyə bilərsiniz'
				),
				'. ',
			];
		case 'el':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
					'με streaming στο'
				),
				getOfferTextBlock(MonetizationType.Ads, ' ή δωρεάν με διαφημίσεις στο'),
				'. ',
			];
		case 'sr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Trenutno možete da gledate', 'strim na'),
				getOfferTextBlock(MonetizationType.Ads, ' ili besplatno sa oglasima na'),
				'. ',
			];
		case 'sv':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'För närvarande kan du se', 'streama på'),
				getOfferTextBlock(MonetizationType.Ads, ' eller gratis med annonser på'),
				'. ',
			];
		case 'uk':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Наразі ви можете дивитися трансляцію', 'на'),
				getOfferTextBlock(MonetizationType.Ads, ' aбо дивитися її безкоштовно з рекламою на'),
				'. ',
			];
		case 'ur':
			return [
				getRTLInfoTextBlock(MonetizationType.Flatrate, 'فی الحال آپ ', 'پر'),
				getOfferTextBlock(
					MonetizationType.Ads,
					' اسٹریمنگ دیکھنے کے قابل ہیں یا',
					'پر اشتہارات کے ساتھ مفت دیکھ سکتے ہیں۔'
				),
			];
		case 'zh':
			return [getRTLInfoTextBlock(MonetizationType.Flatrate, '目前你可以在', '免费带广告观看')];
		case 'en':
		default:
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Currently you are able to watch', 'streaming on'),
				getOfferTextBlock(MonetizationType.Ads, ' or for free with ads on'),
				'. ',
			];
	}
});
</script>
